'use client'

import { motion } from "framer-motion"
import { Crown, Globe2, Sparkles, Heart } from "lucide-react"
import { SITE_CONFIG } from "@/lib/site-config"
import { OrnamentalDivider } from "./ornamental-divider"
import { Badge } from "@/components/ui/badge"

const pillars = [
  {
    icon: Crown,
    title: "Excellence & Bourgeoisie",
    desc: "Une sélection rigoureuse de produits d'exception, à la limite du luxe, où chaque pièce raconte un savoir-faire artisanal transmis de génération en génération.",
  },
  {
    icon: Globe2,
    title: "Universel & Patriote",
    desc: "Neuf rayons réunissant les trésors du Maroc, du Sénégal, de Perse, d'Indonésie, du Levant, du Hedjaz, de Tanzanie et de Malaisie — un hommage à la diversité du monde.",
  },
  {
    icon: Sparkles,
    title: "Émerveillement",
    desc: "Chaque visite est une expérience : parfums d'encens, soieries chatoyantes, cuirs gravés, calligraphies et bijoux d'or. Le beau au service du quotidien.",
  },
  {
    icon: Heart,
    title: "Savoir & Spiritualité",
    desc: "Une maison profondément enracinée dans les valeurs islamiques de générosité, d'hospitalité (Téranga) et de respect du savoir transmis par les gens de savoir.",
  },
]

export function HistorySection() {
  return (
    <section
      id="histoire"
      className="py-20 md:py-28 relative overflow-hidden texture-paper"
    >
      {/* Decorative pattern — calligraphic ornament */}
      <div className="absolute inset-0 opacity-[0.025] pointer-events-none">
        <div className="absolute top-10 left-10 font-display text-[20rem] text-cuir leading-none">﷽</div>
      </div>
      {/* Subtle arabesque overlay */}
      <div className="absolute inset-0 overlay-arabesque opacity-30 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section header with ornamental divider */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <Badge className="mb-5 bg-gold/10 text-gold border border-gold/40 hover:bg-gold/20 tracking-luxe-wide">
            Notre Histoire
          </Badge>
          <h2 className="font-display text-4xl md:text-6xl font-bold text-cuir mb-5 tracking-luxe">
            Une maison née d'un <span className="text-gradient-gold italic">rêve universel</span>
          </h2>
          <div className="max-w-xl mx-auto">
            <OrnamentalDivider variant="star" />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">
          {/* Left - Founder */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="relative">
              {/* Ornamental frame — double gold filet */}
              <div className="absolute -inset-4 border border-gold/30 rounded-2xl pointer-events-none" />
              <div className="absolute -inset-2 border-2 border-gold/40 rounded-xl pointer-events-none" />
              {/* Corner ornaments */}
              <svg className="absolute -top-3 -left-3 h-6 w-6 text-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M2 22 L2 2 L22 2" />
                <path d="M2 12 L12 12 L12 2" />
              </svg>
              <svg className="absolute -top-3 -right-3 h-6 w-6 text-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M22 22 L22 2 L2 2" />
                <path d="M22 12 L12 12 L12 2" />
              </svg>
              <svg className="absolute -bottom-3 -left-3 h-6 w-6 text-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M2 2 L2 22 L22 22" />
                <path d="M2 12 L12 12 L12 22" />
              </svg>
              <svg className="absolute -bottom-3 -right-3 h-6 w-6 text-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M22 2 L22 22 L2 22" />
                <path d="M22 12 L12 12 L12 22" />
              </svg>

              {/* Founder block — emerald with leather texture overlay */}
              <div className="relative gradient-emerald-gold rounded-xl p-8 md:p-12 text-primary-foreground overflow-hidden">
                {/* Decorative top & bottom gold filets */}
                <div className="absolute top-0 left-0 right-0 h-1 gradient-gold" />
                <div className="absolute bottom-0 left-0 right-0 h-1 gradient-gold" />
                {/* Leather grain overlay for tactile depth */}
                <div className="absolute inset-0 overlay-moucharabieh opacity-15 pointer-events-none" />

                <Crown className="h-10 w-10 text-gold mb-4 relative" />
                <p className="text-[11px] uppercase tracking-luxe-wide text-gold mb-2 relative">Fondatrice</p>
                <h3 className="font-display text-3xl md:text-4xl font-semibold mb-3 tracking-luxe relative">
                  La Princesse<br />Alimatou Sadiya Ndiaye
                </h3>
                <p className="text-sm text-primary-foreground/85 italic mb-6 relative tracking-luxe">
                  {SITE_CONFIG.founderTitle}
                </p>
                <blockquote className="text-base md:text-lg leading-[1.8] font-display italic border-l-2 border-gold pl-4 relative">
                  « Le plus joli, le plus islamique et le plus attirant au monde.
                  Le plus créatif, le plus savant, le plus patriote et le plus émerveillant. »
                </blockquote>
                <p className="mt-4 text-xs text-primary-foreground/70 text-right relative tracking-luxe">— Sa devise</p>
              </div>
            </div>
          </motion.div>

          {/* Right - Story */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="space-y-5 text-foreground/85 leading-[1.8]">
              <p className="lettrine">
                Au cœur du 10e arrondissement de Paris, à deux pas du métro Stalingrad et du
                Boulevard de la Villette, <strong className="text-cuir">SADIYA STYLE CUIR</strong> est
                né d'une vision : réunir sous un même toit les plus beaux trésors du monde, du Maroc
                ancestral à l'Indonésie insulaire, de la Perse savante à l'Afrique contemporaine.
              </p>
              <p>
                Notre fondatrice, <strong className="text-cuir">La Princesse Alimatou Sadiya Ndiaye</strong>,
                a conçu chaque rayon comme une invitation au voyage. Le visiteur ne se rend pas
                simplement dans un supermarché : il traverse les souks de Casablanca, les ateliers
                d'Ispahan, les marchés de Zanzibar et les ruelles de Médine.
              </p>
              <p>
                Chaque produit est choisi avec soin pour son authenticité, sa qualité et l'histoire
                qu'il porte. Du bazin riche du Sénégal aux dattes Ajwa de Médine, du café Touba aux
                tanzanites de l'Océan Indien, nous célébrons la richesse des patrimoines et la
                rencontre des cultures.
              </p>
            </div>

            {/* Manuscript-style signature */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-8 flex items-end gap-3"
            >
              <div className="flex-1">
                <div className="h-px bg-gradient-to-r from-gold/40 to-transparent" />
              </div>
              <div className="text-center px-2">
                <p className="font-display italic text-gold text-lg leading-tight">Sadiya</p>
                <p className="text-[10px] uppercase tracking-luxe-wide text-foreground/50 mt-0.5">
                  Maison fondatrice · Paris
                </p>
              </div>
              <div className="flex-1">
                <div className="h-px bg-gradient-to-l from-gold/40 to-transparent" />
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Pillars section header */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="text-[11px] uppercase tracking-luxe-wide text-gold mb-3">Les quatre piliers de la Maison</p>
          <div className="max-w-md mx-auto">
            <OrnamentalDivider variant="knot" />
          </div>
        </motion.div>

        {/* Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map((p, idx) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="group relative p-7 rounded-xl bg-card border border-gold/20 hover:border-gold/50 hover:shadow-luxe-deep transition-all overflow-hidden"
            >
              {/* Corner ornaments */}
              <svg className="absolute top-2 left-2 h-3 w-3 text-gold/40 group-hover:text-gold transition-colors" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M1 11 L1 1 L11 1" />
              </svg>
              <svg className="absolute top-2 right-2 h-3 w-3 text-gold/40 group-hover:text-gold transition-colors" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M11 11 L11 1 L1 1" />
              </svg>
              <svg className="absolute bottom-2 left-2 h-3 w-3 text-gold/40 group-hover:text-gold transition-colors" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M1 1 L1 11 L11 11" />
              </svg>
              <svg className="absolute bottom-2 right-2 h-3 w-3 text-gold/40 group-hover:text-gold transition-colors" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M11 1 L11 11 L1 11" />
              </svg>

              <div className="relative h-14 w-14 rounded-full gradient-gold flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-gold">
                <p.icon className="h-7 w-7 text-cuir" />
              </div>
              <h3 className="font-display text-xl font-semibold text-cuir mb-3 tracking-luxe">{p.title}</h3>
              <p className="text-sm text-foreground/75 leading-[1.7]">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
